/**
 * sl-728o.3.1: Automation Session Registration Hook
 *
 * Registers all automation tutorial sessions in the TutorialRegistry.
 * Called from automation-panel.tsx.
 */
import { useI18n } from '@affine/i18n';
import { useService } from '@toeverything/infra';
import { useEffect } from 'react';

import { TutorialRegistry } from '../../services/tutorial-registry';
import { createInputBatchSession } from './input-batch';
import { createInputFieldsSession } from './input-fields';
import { createInputFilterSession } from './input-filter';
import { createInputRelatedSession } from './input-related';
import { createInputSourceSession } from './input-source';
import { createOutputMappingSession } from './output-mapping';
import { createPermissionOverviewSession } from './permission-overview';
import { createProcessAdvancedSession } from './process-advanced';
import { createProcessTriggerSession } from './process-trigger';
import { createSmartLookupSession } from './smart-lookup';
import { createStatusTrackingSession } from './status-tracking';

// sl-1ahs: output-advanced.ts and multi-output.ts were deleted together
// with their targeted UI surfaces (the Advanced > Response Preview section
// and the multi-output cards grid) in sl-1ahs.6.4.

export function useRegisterAutomationSessions(): void {
  const registry = useService(TutorialRegistry);
  const t = useI18n();

  useEffect(() => {
    const sessions = [
      createInputSourceSession(t),
      createInputFilterSession(t),
      createInputFieldsSession(t),
      createInputBatchSession(t),
      createInputRelatedSession(t),
      createOutputMappingSession(t),
      createProcessTriggerSession(t),
      createProcessAdvancedSession(t),
      createStatusTrackingSession(t),
      createSmartLookupSession(t),
      // sl-kqzl.5: Permission overview coachmark (shown once after first setup)
      createPermissionOverviewSession(t),
    ];
    sessions.forEach(s => registry.register(s));
    return () => sessions.forEach(s => registry.unregister(s.id));
  }, [registry, t]);
}
